import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Award, Lock } from 'lucide-react';
import SEO from '../components/SEO';
import Breadcrumbs from '../components/Breadcrumbs';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const BADGE_ICONS = {
  ilk_konu: '💬',
  ilk_yorum: '✍️',
  on_yorum: '⭐',
  aktif_uye: '🏆',
  etkinlik_katilimcisi: '📅',
  l6e_uzmani: '🟢',
  l7e_uzmani: '🔵',
};

const CATEGORY_LABELS = {
  forum: 'Forum',
  event: 'Etkinlik',
  special: 'Özel',
};

const CATEGORY_COLORS = {
  forum: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  event: 'bg-cyan-50 text-cyan-700 border-cyan-200',
  special: 'bg-violet-50 text-violet-700 border-violet-200',
};

function formatDate(iso) {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString('tr-TR', { day: 'numeric', month: 'long', year: 'numeric' });
}

export default function BadgesPage() {
  const { user } = useAuth();
  const [allBadges, setAllBadges] = useState([]);
  const [myBadges, setMyBadges] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchBadges = async () => {
      setLoading(true);
      try {
        const r = await axios.get(`${API}/badges`);
        setAllBadges(r.data.badges || []);
        if (user) {
          const r2 = await axios.get(`${API}/badges/my`, { withCredentials: true });
          setMyBadges(r2.data.badges || []);
        }
      } finally {
        setLoading(false);
      }
    };
    fetchBadges();
  }, [user]);

  const earnedCount = myBadges.filter(b => b.earned).length;
  const totalCount = allBadges.length;

  const displayBadges = user ? myBadges : allBadges.map(b => ({ ...b, earned: false }));

  const grouped = displayBadges.reduce((acc, b) => {
    const cat = b.category || 'forum';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(b);
    return acc;
  }, {});

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <SEO
        title="Rozetler · Topluluk Başarıları"
        description="MiniOto topluluğunda kazanılabilecek rozetler ve başarım sistemi. Aktif üyeler, uzmanlar ve katkıda bulunanlar."
      />
      <Breadcrumbs items={[{ name: 'Rozetler' }]} />
      <div className="mb-8">
        <h1 className="text-3xl font-extrabold text-slate-800" style={{ fontFamily: 'Outfit, sans-serif' }}>Rozetler</h1>
        <p className="text-slate-500 mt-1">Toplulukta aktif olarak rozet kazanın</p>
      </div>

      {user && (
        <div className="bg-gradient-to-r from-emerald-500 to-teal-500 rounded-2xl p-6 mb-8 text-white">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center">
              <Award className="w-9 h-9" />
            </div>
            <div>
              <h2 className="text-xl font-bold">Rozet Durumunuz</h2>
              <p className="text-emerald-100">{earnedCount} / {totalCount} rozet kazanıldı</p>
              <div className="mt-2 h-2 bg-white/20 rounded-full overflow-hidden w-48">
                <div
                  className="h-full bg-white rounded-full transition-all duration-500"
                  style={{ width: totalCount > 0 ? `${(earnedCount / totalCount) * 100}%` : '0%' }}
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-16">
          <div className="w-10 h-10 border-4 border-emerald-200 border-t-emerald-600 rounded-full animate-spin" />
        </div>
      ) : (
        <div className="space-y-8">
          {Object.entries(grouped).map(([category, badges]) => (
            <div key={category}>
              <h2 className="text-lg font-bold text-slate-700 mb-4 flex items-center gap-2">
                <span className={`text-xs px-2.5 py-1 rounded-full border font-semibold ${CATEGORY_COLORS[category] || 'bg-slate-50 text-slate-600 border-slate-200'}`}>
                  {CATEGORY_LABELS[category] || category}
                </span>
              </h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                {badges.map(badge => (
                  <div
                    key={badge.key}
                    data-testid={`badge-${badge.key}`}
                    className={`rounded-2xl border p-5 text-center transition-all duration-200 ${
                      badge.earned
                        ? `${CATEGORY_COLORS[badge.category] || 'bg-slate-50 text-slate-700 border-slate-200'} shadow-sm`
                        : 'bg-slate-50 border-slate-200 opacity-50'
                    }`}
                  >
                    <div className="text-3xl mb-2">
                      {badge.earned ? (BADGE_ICONS[badge.key] || '🏅') : <Lock className="w-8 h-8 text-slate-300 mx-auto" />}
                    </div>
                    <div className="font-bold text-sm">{badge.name}</div>
                    <div className="text-xs opacity-70 mt-1 leading-tight">{badge.description}</div>
                    {badge.earned && badge.earned_at && (
                      <div className="text-xs opacity-60 mt-2 font-medium">{formatDate(badge.earned_at)}</div>
                    )}
                    {!badge.earned && (
                      <div className="text-xs text-slate-400 mt-2">Kilitli</div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
